import React, { useCallback, useEffect, useMemo } from "react";

import { Box, CircularProgress } from "@mui/material";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { TemplateInfo } from "@fartherfinance/frontend/api/TaskWorkflows/Types";

import { useFormSubmit } from "../context/FormSubmitContext";
import {
  InitialFieldDataProvider,
  useInitialFieldData,
} from "../data/context/InitialFieldDataContext";
import TemplateField from "../fields/components/TemplateField";

import {
  analyzeTemplateFields,
  collectVisibilityDependencies,
  FieldAnalysis,
  isFieldVisible,
} from "./formEngineUtils";
import { ExternalResources, FormData } from "./types";

interface FormEngineProps {
  template: TemplateInfo;
  onSubmit: (data: FormData) => void;
  onError?: (error: Error) => void;
  externalResources?: ExternalResources;
  initialValues?: Record<string, unknown>;
}

const FormEngineCore = ({
  template,
  onSubmit,
  onError,
  externalResources,
  initialValues: _initialValues,
}: FormEngineProps): JSX.Element => {
  const { allOptionsLoaded, processedInitialValues } = useInitialFieldData();
  const { registerSubmit, updateDirtyState } = useFormSubmit();

  const methods = useForm<FormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,
  });

  const { control, getValues, handleSubmit, formState } = methods;

  useEffect(() => {
    if (processedInitialValues && allOptionsLoaded) {
      methods.reset(processedInitialValues, {
        keepDefaultValues: false,
      });

      // After reset with initial data, treat this as the starting point
      updateDirtyState(false);
    }
  }, [allOptionsLoaded, processedInitialValues, methods, updateDirtyState]);
  const handleFormSubmit = useCallback(
    (data: FormData) => {
      try {
        onSubmit(data);
      } catch (error) {
        console.error("[FormEngine] Error during form submission:", error);
        if (onError && error instanceof Error) {
          onError(error);
        }
      }
    },
    [onSubmit, onError]
  );

  useEffect(() => {
    const submitFunction = () => handleSubmit(handleFormSubmit)();
    registerSubmit(submitFunction);
  }, [registerSubmit, handleSubmit, handleFormSubmit]);

  useEffect(() => {
    updateDirtyState(formState.isDirty);
  }, [updateDirtyState, formState.isDirty]);
  const fieldAnalyses = useMemo<FieldAnalysis[]>(() => {
    return analyzeTemplateFields(template);
  }, [template]);
  const visibilityDependencies = useMemo(() => {
    return collectVisibilityDependencies(fieldAnalyses);
  }, [fieldAnalyses]);
  const visibilityDepsWatch = useWatch({
    control,
    name: visibilityDependencies,
  });

  const formContent = useMemo(() => {
    return fieldAnalyses
      .map(({ parameter: p, key, analysis }) => {
        const fieldIsVisible = isFieldVisible(p, getValues, externalResources);
        if (!fieldIsVisible) {
          return null;
        }

        return (
          <Box
            key={key}
            sx={{
              width: {
                xs: "100%",
                sm:
                  "fullWidth" in p.display && p.display.fullWidth
                    ? "100%"
                    : "50%",
              },
              padding: 1,
              boxSizing: "border-box",
            }}
          >
            <TemplateField
              fieldParameter={p}
              externalResources={externalResources}
              fieldAnalysis={analysis}
            />
          </Box>
        );
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldAnalyses, getValues, visibilityDepsWatch, externalResources]);
  if (_initialValues && !allOptionsLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 4,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            margin: -1,
          }}
        >
          {formContent}
        </Box>
      </form>
    </FormProvider>
  );
};

FormEngineCore.displayName = "FormEngineCore";
const FormEngine = (props: FormEngineProps): JSX.Element => {
  return (
    <InitialFieldDataProvider
      template={props.template}
      initialValues={props.initialValues}
    >
      <FormEngineCore {...props} />
    </InitialFieldDataProvider>
  );
};

FormEngine.displayName = "FormEngine";

export default React.memo(FormEngine);
